import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, Power, PowerOff, Sparkles, ArrowRight, Zap, X, CheckCircle2, Network } from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import EcosystemDeployModal from "@/components/EcosystemDeployModal";
import EcosystemGraphModal from "@/components/EcosystemGraphModal";

/**
 * AgentActionCards — affiche les actions structurées émises par NOAM
 * dans la conversation Intelligence V2.
 *
 * Types d'actions supportés :
 *  - navigate              → bouton "M'y emmener"
 *  - suggest_activation    → bouton "Activer ce module"
 *  - suggest_desactivation → bouton "Désactiver ce module"
 *  - prefill_create        → bouton "Pré-remplir" (modale review) + V23.15.27 bouton "Créer directement" (popup confirm + POST)
 *
 * Le prefill est stocké en sessionStorage sous la clé
 * `noam_prefill_<module>` que la page cible lit au mount.
 *
 * V23.15.27 — Doctrine Boutons Action étape A : "Créer directement"
 * Mapping module → endpoint CRUD pour création immédiate via popup confirmation.
 */

// V23.15.27 — Map module → endpoint API pour création directe
const MODULE_CREATE_ENDPOINTS = {
  risques: "/workspace/risks",
  annuaire: "/workspace/annuaire/entities",
  origination: "/workspace/origination/opportunities",
  negociation: "/workspace/negociations",
  plans: "/workspace/plans",
  fournisseurs: "/workspace/fournisseurs",
  controles: "/workspace/controles",
  incidents: "/workspace/incidents",
  alertes: "/workspace/alertes",
  filtres: "/workspace/filtres",
};

// Normalisation déterministe du prefill avant POST : comble les champs requis
// que l'agent exprime sous leur variante « nette » (anciens messages inclus).
const _normalizePrefill = (module, prefill) => {
  const p = { ...(prefill || {}) };
  const clamp15 = (v) => { const n = parseInt(v, 10); return Number.isNaN(n) ? 3 : Math.min(5, Math.max(1, n)); };
  if (module === "risques") {
    if (p.impact_brut == null && p.impact_net != null) p.impact_brut = p.impact_net;
    if (p.probabilite_brute == null && p.probabilite_nette != null) p.probabilite_brute = p.probabilite_nette;
    p.impact_brut = clamp15(p.impact_brut ?? 3);
    p.probabilite_brute = clamp15(p.probabilite_brute ?? 3);
    if (!p.categorie) p.categorie = "operationnel";
  }
  if (module === "plans") {
    // PlanIn exige une échéance (min 4 chars) : défaut J+90 si NOAM ne la propose pas.
    if (!p.echeance || String(p.echeance).trim().length < 4) {
      p.echeance = new Date(Date.now() + 90 * 24 * 3600 * 1000).toISOString().slice(0, 10);
    }
  }
  if (module === "origination") {
    if (!p.titre && p.title) { p.titre = p.title; delete p.title; }
    const t = parseFloat(p.ticket_mEUR);
    p.ticket_mEUR = Number.isNaN(t) ? 0 : Math.max(0, t);
  }
  if (module === "negociation" && !p.title && p.titre) { p.title = p.titre; delete p.titre; }
  if (module === "annuaire" && !p.name && (p.nom || p.titre)) { p.name = p.nom || p.titre; delete p.nom; delete p.titre; }
  if (module === "fournisseurs" && !p.nom && (p.name || p.titre)) { p.nom = p.name || p.titre; delete p.name; delete p.titre; }
  return p;
};

// Label humain par module pour le toast de confirmation
const MODULE_HUMAN_LABEL = {
  risques: "risque",
  annuaire: "entité",
  origination: "opportunité",
  negociation: "négociation",
  plans: "plan d'action",
  fournisseurs: "fournisseur",
  controles: "contrôle",
  incidents: "incident",
  alertes: "alerte",
  filtres: "filtre",
};

export default function AgentActionCards({ actions, onAction }) {
  const navigate = useNavigate();
  // V23.15.27 — État du popup de confirmation pour "Créer directement"
  const [confirmDirect, setConfirmDirect] = useState(null); // { action: a } ou null
  const [creating, setCreating] = useState(false);
  // V23.15.27 — État du récap bulk_deploy (étape C)
  const [bulkDeploy, setBulkDeploy] = useState(null); // { action: a } ou null
  const [bulkBusy, setBulkBusy] = useState(false);
  // P1 2026-06 — État de la grappe ontologique
  const [ecoDeploy, setEcoDeploy] = useState(null); // { action: a } ou null
  const [ecoBusy, setEcoBusy] = useState(false);
  // P2 2026-09 — Schéma dérivé du déploiement (ouvert automatiquement après succès)
  const [ecoGraphId, setEcoGraphId] = useState(null);

  if (!actions || actions.length === 0) return null;

  const handleNavigate = (a) => {
    if (!a.route) return;
    navigate(a.route);
    if (onAction) onAction(a);
  };

  const handleToggle = async (a, enable) => {
    try {
      await api.post("/workspace/modules/toggle", {
        module: a.module,
        enable,
      });
      notify.success(
        enable
          ? `Module ${a.module_label || a.module} activé`
          : `Module ${a.module_label || a.module} désactivé`
      );
      if (onAction) onAction(a);
    } catch (e) {
      notify.error(fmtError(e, "Toggle failed"));
    }
  };

  const handlePrefill = (a) => {
    if (!a.module || !a.prefill) return;
    try {
      sessionStorage.setItem(
        `noam_prefill_${a.module}`,
        JSON.stringify({
          open_action: a.open_action || "create",
          data: a.prefill,
          source: "intelligence_v2",
          created_at: Date.now(),
        })
      );
    } catch (e) {
      // sessionStorage may be full — fail silently
    }
    navigate(a.route);
    if (onAction) onAction(a);
  };

  // V23.15.27 — Étape A : créer directement après confirmation.
  // Ouvre un popup qui montre le récap de ce qui va être créé.
  const handleCreateDirectly = (a) => {
    if (!a.module || !a.prefill) return;
    setConfirmDirect({ action: a });
  };

  const executeCreateDirectly = async () => {
    if (!confirmDirect?.action) return;
    const a = confirmDirect.action;
    const endpoint = MODULE_CREATE_ENDPOINTS[a.module];
    if (!endpoint) {
      notify.error(`Module ${a.module} non supporté pour la création directe.`);
      setConfirmDirect(null);
      return;
    }
    setCreating(true);
    try {
      const r = await api.post(endpoint, _normalizePrefill(a.module, a.prefill));
      const label = MODULE_HUMAN_LABEL[a.module] || "entrée";
      const title = _extractTitle(a.prefill);
      notify.success(`${_capitalize(label)} créé${label.endsWith("e") ? "" : ""}${title ? ` : ${title}` : ""}.`, {
        action: a.route ? {
          label: "Voir",
          onClick: () => navigate(a.route),
        } : undefined,
      });
      setConfirmDirect(null);
      if (onAction) onAction({ ...a, _created_id: r?.data?.id });
    } catch (e) {
      if (e?.response?.status === 422) {
        const missing = (Array.isArray(e.response?.data?.detail) ? e.response.data.detail : [])
          .filter((d) => d?.type === "missing")
          .map((d) => (d.loc || []).slice(-1)[0])
          .join(", ");
        notify.error(
          missing
            ? `Champ requis manquant : ${missing}. Utilisez « Pré-remplir » pour compléter le formulaire.`
            : "Certains champs requis manquent. Utilisez « Pré-remplir » pour compléter le formulaire."
        );
      } else {
        notify.error(fmtError(e, "Création impossible."));
      }
    } finally {
      setCreating(false);
    }
  };

  // V23.15.27 — Étape B : lancer l'analyse pour module IA-only.
  const handleLaunchAnalysis = async (a) => {
    if (!a.start_endpoint) {
      notify.error("Endpoint d'analyse manquant.");
      return;
    }
    try {
      const params = { ...(a.analyze_params || {}) };
      if (a.mode_default && !params.mode) params.mode = a.mode_default;
      const r = await api.post(a.start_endpoint, null, { params });
      const jobId = r?.data?.job_id;
      if (jobId && a.job_endpoint_pattern) {
        notify.success(`Analyse lancée — résultats dans 30-60s.`, {
          action: a.route ? { label: "Ouvrir", onClick: () => navigate(a.route) } : undefined,
        });
      } else {
        notify.success("Analyse déclenchée.", {
          action: a.route ? { label: "Ouvrir", onClick: () => navigate(a.route) } : undefined,
        });
      }
      if (onAction) onAction(a);
    } catch (e) {
      notify.error(fmtError(e, "Analyse impossible."));
    }
  };

  // V23.15.27 — Étape C : ouvrir le récap bulk_deploy avec checkboxes.
  const handleBulkDeploy = (a) => {
    if (!a.items || a.items.length === 0) return;
    setBulkDeploy({ action: a });
  };

  const executeBulkDeploy = async (selectedItems) => {
    if (!selectedItems || selectedItems.length === 0) {
      setBulkDeploy(null);
      return;
    }
    // V23.15.27 — capture the action ref before clearing state (safer closure)
    const currentAction = bulkDeploy?.action;
    setBulkBusy(true);
    const results = { created: 0, failed: 0, details: [] };
    for (const item of selectedItems) {
      const endpoint = MODULE_CREATE_ENDPOINTS[item.module];
      if (!endpoint) {
        results.failed += 1;
        results.details.push(`${item.module}: non supporté`);
        continue;
      }
      try {
        await api.post(endpoint, _normalizePrefill(item.module, item.prefill));
        results.created += 1;
      } catch (e) {
        results.failed += 1;
        results.details.push(`${item.module}: ${e?.response?.data?.detail || e?.message || "erreur"}`);
      }
    }
    setBulkBusy(false);
    setBulkDeploy(null);
    if (results.created > 0) {
      notify.success(
        `${results.created} création(s) effectuée(s)${results.failed > 0 ? ` · ${results.failed} échec(s)` : ""}.`
      );
    } else if (results.failed > 0) {
      notify.error(`Toutes les créations ont échoué (${results.failed}).`);
    }
    // V23.15.27 — surface per-item failures so user knows WHICH items failed
    if (results.failed > 0 && results.details.length > 0) {
      const detail = results.details.slice(0, 4).join(" · ");
      notify.error(`Échecs : ${detail}${results.details.length > 4 ? ` (+${results.details.length - 4})` : ""}`);
    }
    if (onAction) onAction({ ...currentAction, _bulk_results: results });
  };

  // P1 2026-06 — Grappe ontologique : ouvrir la modale de validation.
  const handleEcosystemDeploy = (a) => {
    if ((a.entities || []).length === 0 && (a.items || []).length === 0) return;
    setEcoDeploy({ action: a });
  };

  const executeEcosystemDeploy = async (selection) => {
    const a = ecoDeploy?.action;
    if (!a) return;
    setEcoBusy(true);
    try {
      const r = await api.post("/workspace/agentic/ecosystem/deploy", {
        session_id: a.session_id || null,
        label: a.label || null,
        reason: a.reason || null,
        ...selection,
      });
      const d = r?.data || {};
      const parts = [];
      if ((d.created_entities || []).length) parts.push(`${d.created_entities.length} entité(s) créée(s)`);
      if ((d.linked_entities || []).length) parts.push(`${d.linked_entities.length} reliée(s) à l'existant`);
      if ((d.created_items || []).length) parts.push(`${d.created_items.length} objet(s)`);
      parts.push(`${d.relationships_created || 0} relation(s)`);
      notify.success(`Écosystème matérialisé : ${parts.join(" · ")}.`);
      if ((d.skipped || []).length > 0) {
        notify.error(
          `Non déployé(s) : ${d.skipped.slice(0, 3).map((s) => s.reason).join(" · ")}${d.skipped.length > 3 ? ` (+${d.skipped.length - 3})` : ""}`
        );
      }
      setEcoDeploy(null);
      // Récompense immédiate : montrer ce que NOAM a appris (schéma dérivé)
      if (d.deployment_id) setEcoGraphId(d.deployment_id);
      if (onAction) onAction({ ...a, _ecosystem_results: d });
    } catch (e) {
      notify.error(fmtError(e, "Déploiement de la grappe impossible."));
    } finally {
      setEcoBusy(false);
    }
  };

  return (
    <div
      className="mt-3 pt-3 border-t border-white/10 space-y-2"
      data-testid="agent-actions-block"
    >
      <div className="text-[10px] text-[#EF9F27] uppercase tracking-wider inline-flex items-center gap-1">
        <Sparkles className="w-3 h-3" />
        Actions suggérées par Noam
      </div>
      <div className="flex flex-col gap-2">
        {actions.map((a, idx) => (
          <ActionCard
            key={`${a.type}-${a.module}-${idx}`}
            action={a}
            onNavigate={handleNavigate}
            onToggle={handleToggle}
            onPrefill={handlePrefill}
            onCreateDirectly={handleCreateDirectly}
            onLaunchAnalysis={handleLaunchAnalysis}
            onBulkDeploy={handleBulkDeploy}
            onEcosystemDeploy={handleEcosystemDeploy}
            onAction={onAction}
          />
        ))}
      </div>

      {/* V23.15.27 — Popup de confirmation pour "Créer directement" */}
      {confirmDirect && (
        <ConfirmCreateModal
          action={confirmDirect.action}
          busy={creating}
          onCancel={() => !creating && setConfirmDirect(null)}
          onConfirm={executeCreateDirectly}
        />
      )}

      {/* V23.15.27 — Récap bulk_deploy avec checkboxes individuelles */}
      {bulkDeploy && (
        <BulkDeployModal
          action={bulkDeploy.action}
          busy={bulkBusy}
          onCancel={() => !bulkBusy && setBulkDeploy(null)}
          onConfirm={executeBulkDeploy}
        />
      )}

      {/* P1 2026-06 — Validation de la grappe ontologique */}
      {ecoDeploy && (
        <EcosystemDeployModal
          action={ecoDeploy.action}
          busy={ecoBusy}
          onCancel={() => !ecoBusy && setEcoDeploy(null)}
          onConfirm={executeEcosystemDeploy}
        />
      )}

      {/* P2 2026-09 — Schéma dérivé affiché après matérialisation */}
      {ecoGraphId && (
        <EcosystemGraphModal deploymentId={ecoGraphId} onClose={() => setEcoGraphId(null)} />
      )}
    </div>
  );
}

function ActionCard({ action: a, onNavigate, onToggle, onPrefill, onCreateDirectly, onLaunchAnalysis, onBulkDeploy, onEcosystemDeploy, onAction }) {
  if (a.type === "navigate") {
    return (
      <div
        className="flex items-center gap-3 p-2.5 rounded-lg bg-[#378ADD]/8 border border-[#378ADD]/25 hover:bg-[#378ADD]/15 transition-colors"
        data-testid={`agent-action-navigate-${a.module}`}
      >
        <Compass className="w-3.5 h-3.5 text-[#378ADD] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-zinc-100 font-medium sm:truncate">
            {a.module_label || a.module}
          </div>
          {a.reason && (
            <div className="text-[10px] text-zinc-300 sm:truncate">{a.reason}</div>
          )}
        </div>
        <button
          onClick={() => onNavigate(a)}
          className="text-[11px] px-2.5 py-1 rounded-md bg-[#378ADD] text-white hover:bg-[#378ADD]/90 inline-flex items-center gap-1 shrink-0"
          data-testid={`agent-action-navigate-btn-${a.module}`}
        >
          M&apos;y emmener
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    );
  }

  if (a.type === "suggest_activation") {
    return (
      <div
        className="flex items-center gap-3 p-2.5 rounded-lg bg-emerald-500/8 border border-emerald-500/25"
        data-testid={`agent-action-activate-${a.module}`}
      >
        <Power className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-zinc-100 font-medium sm:truncate">
            Activer {a.module_label || a.module}
          </div>
          {a.reason && (
            <div className="text-[10px] text-zinc-300 sm:truncate">{a.reason}</div>
          )}
        </div>
        <button
          onClick={() => onToggle(a, true)}
          className="text-[11px] px-2.5 py-1 rounded-md bg-emerald-500 text-white hover:bg-emerald-600 shrink-0"
          data-testid={`agent-action-activate-btn-${a.module}`}
        >
          Activer
        </button>
      </div>
    );
  }

  if (a.type === "suggest_desactivation") {
    return (
      <div
        className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-500/8 border border-zinc-500/25"
        data-testid={`agent-action-deactivate-${a.module}`}
      >
        <PowerOff className="w-3.5 h-3.5 text-zinc-300 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-zinc-100 font-medium sm:truncate">
            Désactiver {a.module_label || a.module}
          </div>
          {a.reason && (
            <div className="text-[10px] text-zinc-300 sm:truncate">{a.reason}</div>
          )}
        </div>
        <button
          onClick={() => onToggle(a, false)}
          className="text-[11px] px-2.5 py-1 rounded-md bg-zinc-700 text-white hover:bg-zinc-600 shrink-0"
          data-testid={`agent-action-deactivate-btn-${a.module}`}
        >
          Désactiver
        </button>
      </div>
    );
  }

  if (a.type === "prefill_create") {
    const fieldCount = a.prefill ? Object.keys(a.prefill).length : 0;
    const directSupported = !!MODULE_CREATE_ENDPOINTS[a.module];
    return (
      <div
        className="flex items-center gap-3 p-2.5 rounded-lg bg-[#EF9F27]/8 border border-[#EF9F27]/25"
        data-testid={`agent-action-prefill-${a.module}`}
      >
        <Sparkles className="w-3.5 h-3.5 text-[#EF9F27] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-zinc-100 font-medium sm:truncate">
            {a.label || `Créer dans ${a.module_label || a.module}`}
          </div>
          <div className="text-[10px] text-zinc-300 sm:truncate">
            {fieldCount} champ(s) pré-rempli(s){a.reason ? ` · ${a.reason}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => onPrefill(a)}
            className="text-[11px] px-2.5 py-1 rounded-md border border-[#EF9F27]/50 text-[#EF9F27] hover:bg-[#EF9F27]/10 font-medium"
            data-testid={`agent-action-prefill-btn-${a.module}`}
            title="Ouvrir la modale avec les champs préremplis (review avant validation)"
          >
            Pré-remplir
          </button>
          {directSupported && (
            <button
              onClick={() => onCreateDirectly(a)}
              className="text-[11px] px-2.5 py-1 rounded-md bg-[#EF9F27] text-zinc-900 hover:bg-[#EF9F27]/90 font-medium inline-flex items-center gap-1"
              data-testid={`agent-action-create-direct-btn-${a.module}`}
              title="Créer immédiatement après confirmation rapide"
            >
              <Zap className="w-3 h-3" />
              Créer directement
            </button>
          )}
        </div>
      </div>
    );
  }

  if (a.type === "launch_analysis") {
    return <_LaunchAnalysisCard action={a} onLaunch={onLaunchAnalysis} />;
  }

  if (a.type === "bulk_deploy") {
    return <_BulkDeployCard action={a} onOpen={onBulkDeploy} />;
  }

  // P1 2026-06 — Grappe ontologique proposée par NOAM
  if (a.type === "deploy_ecosystem") {
    return <_EcosystemCard action={a} onOpen={onEcosystemDeploy} />;
  }

  // V23.15.35 — Wizard cockpit conversationnel : Noam propose et l'utilisateur ouvre en 1 clic
  if (a.type === "configure_cockpit") {
    return (
      <div
        className="flex items-center gap-3 p-2.5 rounded-lg bg-gradient-to-br from-[#EF9F27]/15 to-[#7F77DD]/10 border border-[#EF9F27]/40"
        data-testid="agent-action-configure-cockpit"
      >
        <Sparkles className="w-3.5 h-3.5 text-[#EF9F27] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-zinc-100 font-medium sm:truncate">
            Configurer ton environnement en 3 questions (30s)
          </div>
          {a.reason && (
            <div className="text-[10px] text-zinc-300 sm:truncate">{a.reason}</div>
          )}
        </div>
        <button
          onClick={() => {
            // Ouvre le wizard via un event global capté par ProactiveCockpitAssistant
            try {
              window.dispatchEvent(new CustomEvent("noam:open-cockpit-wizard"));
            } catch { /* ignore */ }
            // Fallback : redirige vers Dashboard où le banner est visible
            setTimeout(() => {
              if (window.location.pathname !== "/app/dashboard") {
                window.location.href = "/app/dashboard";
              }
            }, 400);
            onAction?.(a);
          }}
          className="shrink-0 text-[11px] px-2.5 py-1.5 rounded-md font-medium bg-[#EF9F27] text-black hover:bg-[#FFB627] transition-colors inline-flex items-center gap-1"
          data-testid="agent-action-configure-cockpit-btn"
        >
          <Sparkles className="w-3 h-3" />
          Démarrer
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return null;
}

// V23.15.27 — Étape B : carte "Lancer l'analyse" (IA-only modules)
function _LaunchAnalysisCard({ action: a, onLaunch }) {
  return (
    <div
      className="flex items-center gap-3 p-2.5 rounded-lg bg-[#7F77DD]/10 border border-[#7F77DD]/30"
      data-testid={`agent-action-launch-${a.module}`}
    >
      <Sparkles className="w-3.5 h-3.5 text-[#7F77DD] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-zinc-100 font-medium sm:truncate">
          {a.label || `Lancer l'analyse ${a.module_label || a.module}`}
        </div>
        {a.reason && (
          <div className="text-[10px] text-zinc-300 sm:truncate">{a.reason}</div>
        )}
      </div>
      <button
        onClick={() => onLaunch(a)}
        className="text-[11px] px-2.5 py-1 rounded-md bg-[#7F77DD] text-white hover:bg-[#7F77DD]/90 font-medium shrink-0 inline-flex items-center gap-1"
        data-testid={`agent-action-launch-btn-${a.module}`}
      >
        <Zap className="w-3 h-3" />
        Lancer l&apos;analyse
      </button>
    </div>
  );
}

// V23.15.27 — Étape C : carte "Tout déployer" (cascade multi-modules)
function _BulkDeployCard({ action: a, onOpen }) {
  return (
    <div
      className="flex items-center gap-3 p-2.5 rounded-lg bg-emerald-500/8 border border-emerald-500/30"
      data-testid="agent-action-bulk-deploy"
    >
      <Sparkles className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-zinc-100 font-medium sm:truncate">
          {a.label || `Déployer ${a.count} création(s) en cascade`}
        </div>
        {a.reason && (
          <div className="text-[10px] text-zinc-300 sm:truncate">{a.reason}</div>
        )}
      </div>
      <button
        onClick={() => onOpen(a)}
        className="text-[11px] px-2.5 py-1 rounded-md bg-emerald-500 text-white hover:bg-emerald-600 font-medium shrink-0 inline-flex items-center gap-1"
        data-testid="agent-action-bulk-deploy-btn"
      >
        Voir & déployer
      </button>
    </div>
  );
}

// P1 2026-06 — Carte grappe ontologique (entités + objets + relations)
function _EcosystemCard({ action: a, onOpen }) {
  const nE = (a.entities || []).length;
  const nI = (a.items || []).length;
  const nR = (a.relationships || []).length;
  const nExisting = (a.entities || []).filter((e) => e.resolution?.status === "existing").length;
  return (
    <div
      className="flex items-center gap-3 p-2.5 rounded-lg bg-[#7F77DD]/10 border border-[#7F77DD]/35"
      data-testid="agent-action-ecosystem"
    >
      <Network className="w-3.5 h-3.5 text-[#7F77DD] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-zinc-100 font-medium sm:truncate">
          {a.label || "Déployer l'écosystème proposé"}
        </div>
        <div className="text-[10px] text-zinc-300 sm:truncate">
          {nE} entité(s){nExisting > 0 ? ` (${nExisting} existante(s))` : ""} · {nI} objet(s) · {nR} relation(s)
          {a.reason ? ` · ${a.reason}` : ""}
        </div>
      </div>
      <button
        onClick={() => onOpen(a)}
        className="text-[11px] px-2.5 py-1 rounded-md bg-[#7F77DD] text-white hover:bg-[#8F88E5] font-medium shrink-0 inline-flex items-center gap-1"
        data-testid="agent-action-ecosystem-btn"
      >
        <Network className="w-3 h-3" />
        Examiner la grappe
      </button>
    </div>
  );
}

// V23.15.27 — Popup de confirmation pour "Créer directement"
function ConfirmCreateModal({ action: a, busy, onCancel, onConfirm }) {
  const label = MODULE_HUMAN_LABEL[a.module] || "entrée";
  const title = _extractTitle(a.prefill);
  const fields = Object.entries(a.prefill || {});
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4"
      onClick={() => !busy && onCancel()}
      data-testid="agent-confirm-create-overlay"
    >
      <div
        className="noam-card w-full max-w-md p-5 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="agent-confirm-create-modal"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="noam-overline text-[#EF9F27]">Confirmation</div>
            <div className="text-base font-semibold text-zinc-100 mt-1">
              Créer ce {label} ?
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="p-1 rounded hover:bg-white/5 text-zinc-400 disabled:opacity-50"
            data-testid="agent-confirm-cancel-x"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {title && (
          <div className="px-3 py-2 rounded-md bg-[#EF9F27]/10 border border-[#EF9F27]/30 text-sm text-zinc-100 mb-3">
            « {title} »
          </div>
        )}

        <div className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1">
          Champs proposés par Noam
        </div>
        <ul
          className="space-y-1 mb-4 text-[12px] max-h-64 overflow-y-auto"
          data-testid="agent-confirm-fields-list"
        >
          {fields.length === 0 && (
            <li className="text-zinc-500 italic">Aucun champ pré-rempli.</li>
          )}
          {fields.map(([k, v]) => (
            <li key={k} className="flex items-start gap-2">
              <span className="text-zinc-500 shrink-0 w-32 truncate">{k}</span>
              <span className="text-zinc-200 break-words">
                {_formatFieldValue(v)}
              </span>
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-[12px] px-3 py-1.5 rounded-md border border-white/10 text-zinc-300 hover:bg-white/5 disabled:opacity-50"
            data-testid="agent-confirm-cancel-btn"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="text-[12px] px-3 py-1.5 rounded-md bg-[#EF9F27] text-zinc-900 hover:bg-[#EF9F27]/90 font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="agent-confirm-create-btn"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            {busy ? "Création…" : "Créer maintenant"}
          </button>
        </div>
      </div>
    </div>
  );
}

function _extractTitle(prefill) {
  if (!prefill || typeof prefill !== "object") return "";
  return (
    prefill.titre || prefill.title || prefill.nom || prefill.name || prefill.label || ""
  );
}

function _formatFieldValue(v) {
  if (v == null) return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function _capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// V23.15.27 — Étape C : modale de récap multi-créations avec checkboxes
function BulkDeployModal({ action: a, busy, onCancel, onConfirm }) {
  const initialItems = (a.items || []).map((it, idx) => ({ ...it, _id: idx, _checked: true }));
  const [items, setItems] = useState(initialItems);

  const toggle = (id) => {
    setItems((arr) => arr.map((it) => (it._id === id ? { ...it, _checked: !it._checked } : it)));
  };
  const selected = items.filter((it) => it._checked);
  const allChecked = items.every((it) => it._checked);

  const handleConfirm = () => {
    onConfirm(selected.map((it) => ({ module: it.module, prefill: it.prefill, route: it.route })));
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4"
      onClick={() => !busy && onCancel()}
      data-testid="agent-bulk-deploy-overlay"
    >
      <div
        className="noam-card w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="agent-bulk-deploy-modal"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="noam-overline text-emerald-400">Tout déployer en cascade</div>
            <div className="text-base font-semibold text-zinc-100 mt-1">
              Sélectionnez les créations à effectuer
            </div>
            {a.reason && (
              <div className="text-[11px] text-zinc-300 mt-1">{a.reason}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="p-1 rounded hover:bg-white/5 text-zinc-400 disabled:opacity-50"
            data-testid="agent-bulk-deploy-cancel-x"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="text-[11px] text-zinc-400 uppercase tracking-wider mb-2 flex items-center justify-between">
          <span>{items.length} création(s) proposée(s) · {selected.length} cochée(s)</span>
          <button
            type="button"
            onClick={() => setItems((arr) => arr.map((it) => ({ ...it, _checked: !allChecked })))}
            className="text-[10px] text-[#378ADD] hover:underline normal-case tracking-normal"
            data-testid="agent-bulk-deploy-toggle-all"
          >
            {allChecked ? "Tout décocher" : "Tout cocher"}
          </button>
        </div>

        <ul className="space-y-2 mb-4" data-testid="agent-bulk-deploy-items-list">
          {items.map((it) => {
            const fields = Object.entries(it.prefill || {});
            const title = _extractTitle(it.prefill);
            return (
              <li
                key={it._id}
                className={`px-3 py-2.5 rounded-md border ${
                  it._checked ? "bg-white/[0.03] border-white/15" : "bg-transparent border-white/5 opacity-50"
                }`}
                data-testid={`agent-bulk-deploy-item-${it.module}`}
              >
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={it._checked}
                    onChange={() => toggle(it._id)}
                    disabled={busy}
                    className="mt-1 w-4 h-4 rounded border-white/20 bg-zinc-900 text-emerald-500 focus:ring-emerald-400/30 cursor-pointer"
                    data-testid={`agent-bulk-deploy-checkbox-${it.module}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">
                        {it.module_label || it.module}
                      </span>
                      {title && (
                        <span className="text-sm text-zinc-100 font-medium sm:truncate">
                          {title}
                        </span>
                      )}
                    </div>
                    {it.reason && (
                      <div className="text-[10px] text-zinc-300 mt-1">{it.reason}</div>
                    )}
                    {fields.length > 0 && (
                      <div className="text-[10px] text-zinc-500 mt-1 truncate" title={fields.map(([k, v]) => `${k}: ${_formatFieldValue(v)}`).join(" · ")}>
                        {fields.slice(0, 4).map(([k, v]) => `${k}=${_formatFieldValue(v)}`).join(" · ")}
                        {fields.length > 4 && ` · +${fields.length - 4} champ(s)`}
                      </div>
                    )}
                  </div>
                </label>
              </li>
            );
          })}
        </ul>

        <div className="flex items-center justify-between gap-2 pt-3 border-t border-white/5">
          <div className="text-[11px] text-zinc-400">
            {selected.length === 0 ? (
              <span className="text-[#EF9F27]">Aucun élément sélectionné</span>
            ) : (
              <span>
                <span className="text-emerald-400 font-medium">{selected.length}</span> sera créé(s) · {items.length - selected.length} ignoré(s)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="text-[12px] px-3 py-1.5 rounded-md border border-white/10 text-zinc-300 hover:bg-white/5 disabled:opacity-50"
              data-testid="agent-bulk-deploy-cancel-btn"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy || selected.length === 0}
              className="text-[12px] px-3 py-1.5 rounded-md bg-emerald-500 text-white hover:bg-emerald-600 font-medium inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="agent-bulk-deploy-confirm-btn"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              {busy ? "Création…" : `Confirmer la sélection (${selected.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
